import { type FC, type ReactNode } from "react";
import styled from "styled-components";

import { useProjectEffect } from "@ds/hooks/use-project-effect";
import { useReduxEntitiesFetch } from "@ds/hooks/use-redux-entities-fetch";

import { EntitySubtypeName, EntityTypeName } from "@ds/model/entity-model";

import { TableFilterOptions } from "@ds/modules/table-data/redux/types";
import { isMainTableDataTypeEnum } from "@ds/modules/table-data/utils/helpers";
import { DetailsTableDataTypeEnum, MainTableDataTypeEnum } from "@ds/modules/table-data/utils/model";

import { TableFilterBar, type TableFilterDefinition } from "@ds/components/table-filters";
import { TableSearchBar } from "@ds/components/table-search-bar";

import { HeaderTitle } from "./header-title";

const Wrapper = styled.div`
  height: 100%;
  display: flex;
`;

const MainContentWrapper = styled.div`
  /* min-width:0 so the table's max-content floor can scroll inside this pane
     instead of expanding the flex row and pushing the info sidebar off-screen. */
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-flow: column;
`;

const ContentBody = styled.div`
  display: flex;
  flex-flow: column;
  margin: 16px;
`;

const TableActionsWrapper = styled.div`
  min-height: 35px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 18px 16px;
`;

const TableQueryWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

export type MainPage = {
  entityType: EntityTypeName & EntitySubtypeName;
  tableType: MainTableDataTypeEnum | DetailsTableDataTypeEnum;
};

export const MainView: FC<{
  options?: ReactNode;
  view: ReactNode;
  info?: ReactNode;
  title: string;
  actions?: ReactNode;
  filterOptions: TableFilterOptions;
  filterDefinitions?: TableFilterDefinition[];
}> = ({
  title,
  options,
  view,
  info,
  actions,
  filterDefinitions,
  filterOptions: { entityType, entitySubtype, tableType, tableTab, queryFilters },
}) => {
  const [, toggleRefetch] = useReduxEntitiesFetch(
    { entityType, entitySubtype, filters: queryFilters },
    { tableType, initialLoad: false },
  );

  useProjectEffect(() => toggleRefetch(), [tableType]);

  const mainTableType = isMainTableDataTypeEnum(tableType) ? tableType : undefined;

  return (
    <>
      <HeaderTitle title={title} tableType={mainTableType} isListPage />
      <Wrapper>
        <MainContentWrapper>
          {options}
          <TableActionsWrapper>
            <TableQueryWrapper>
              <TableSearchBar
                entityType={entityType}
                tableType={tableType}
                tableTab={tableTab}
                queryFilters={queryFilters}
              />

              {filterDefinitions && (
                <TableFilterBar
                  tableType={tableType}
                  tableTab={tableTab}
                  queryFilters={queryFilters}
                  definitions={filterDefinitions}
                />
              )}
            </TableQueryWrapper>

            {actions}
          </TableActionsWrapper>
          <ContentBody>{view}</ContentBody>
        </MainContentWrapper>

        {!!info && <>{info}</>}
      </Wrapper>
    </>
  );
};
